import moment from 'moment';
import Decimal from 'decimal.js';
import Model from './model.js';
import Log from 'lib/log';
import Employee from './employee.js';

const log = Log('newModels/payroll-detail');
const excludedOvertime = ['Overtime (Wage)'] // ticket: PPKAN-340
export default class PayrollDetail extends Model {
    urlRoot() { return '/PayrollDetails'; }

    constructor() {
        super(...arguments);
        this.employee = null;
        this.softDelete = false;
    }

    defaults() {
        return {
            amount: 0,
            rate: 0,
            unit: 0,
            isAdditional: true,
            isLocked: false
        };
    }

    constraints() {
        return {
            employeeId: {presence: true},
            payrollItemId: {presence: true},
            isLocked: {presence: true},
            isAdditional: {presence: true},
            amount: {
                presence: true
            },
            unit: {
                numericality: {greaterThanOrEqualTo: 0}
            },
            additionalInformation: function(value, attrs) {
                if (!attrs.payrollItemId) {
                    return null;
                }

                if (!app.payrollItems) {
                    return null;
                }

                const payrollItem = app.payrollItems.get(attrs.payrollItemId);
                if (!payrollItem || !payrollItem.get('additionalInfoClass')) {
                    return null;
                }

                return {presence: true};
            }
        };
    }

    initialize() {
        this.idAttribute = 'objectId';
        super.initialize(...arguments);
        this.payrollItem = this.getPayrollItem();
        this.affectsItem = this.getAffectsItem();
        this.on('change:payrollItemId', this._setPayrollItem, this);
        this.on('change:affectsItem', this._setAffectsItem, this);
        this.on('change:rate change:unit', this.updateAmount, this);
        return this;
    }

    getPayrollItem() {
        if (this.payrollItem) {
            return this.payrollItem;
        }
        return this._setPayrollItem(this.get('payrollItemId'));
    }

    _setPayrollItem(id) {
        if (!app.payrollItems) {
            log.warn(`App hasn't any payroll items loaded`);
            return;
        }

        if (!id) {
            log.warn(`PayrollDetail doesn't have a payroll item`);
            return;
        }

        this.payrollItem = app.payrollItems.find(model => {
            return model.get('objectId') === id;
        });

        return this.payrollItem;
    }

    getAffectsItem() {
        if (this.affectsItem) {
            return this.affectsItem;
        }

        return this._setAffectsItem(this.get('affectsItemId'));
    }

    _setAffectsItem(id) {
        if (!app.payrollItems) {
            log.warn(`App hasn't any payroll items loaded`);
            return;
        }

        if (!id) {
            return;
        }

        this.affectsItem = app.payrollItems.find(model => {
            return model.get('objectId') === id;
        });

        return this.affectsItem;
    }

    fetchEmployee() {
        if (!this.get('employeeId')) {
            return null;
        }

        const employee = app.employees.find(
            emp => emp.id === this.get('employeeId')
        );
        if (employee) {
            this.employee = employee;
            return Promise.resolve(employee);
        }

        this.employee = new Employee({objectId: this.get('employeeId')});
        return this.employee.fetch();
    }

    getEmployee() {
        return this.employee;
    }

    updateAmount() {
        if (this.isUnit() || this.isOvertime()) {
            const roundedRate = new Decimal(this.get('rate'))
                .toDP(2, Decimal.ROUND_HALF_UP);
            const roundedUnit = new Decimal(this.get('unit'))
                .toDP(2, Decimal.ROUND_HALF_UP);

            this.set(
                'amount',
                roundedRate.times(roundedUnit)
                    .toDP(2, Decimal.ROUND_HALF_UP)
                    .toNumber()
            );
        }
    }

    getPayrollItemName() {
        const item = this.getPayrollItem();
        const affects = this.getAffectsItem();
        let payrollItemName = (item && item.get('name')) || '';

        if (affects && affects.get('name')) {
            payrollItemName += ` on ${affects.get('name')}`;
        }

        return payrollItemName;
    }

    getPayrollItemType() {
        const item = this.getPayrollItem();
        return item && item.get('type');
    }

    isUnpaidLeave() {
        const item = this.getPayrollItem();
        return item && item.get('type') === 'unpaid_leave';
    }

    isUnit() {
        const item = this.getPayrollItem();
        return item.get('isUnit') === true;
    }

    isZakat() {
        const item = this.getPayrollItem();
        return item.get('type') === 'zakat';
    }

    isOvertime() {
        const item = this.getPayrollItem();
        return (item && item.get('type') === 'overtime' &&
        !excludedOvertime.includes(item.get('name')));
    }

    isLocked() {
        return this.get('isLocked') === true;
    }

    isAdditional() {
        return this.get('isAdditional') === true;
    }

    isAddition() {
        const item = this.getPayrollItem();
        return (
            item &&
            _.contains(
                ['addition', 'bonus', 'base', 'overtime'],
                item.get('type')
            ) &&
            (item.get('isGross') || item.get('isNet'))
        );
    }

    isDeduction() {
        const item = this.getPayrollItem();
        return (
            item &&
            _.contains(
                [
                    'unpaid_leave', 'deduction', 'mtd', 'epf', 'socso',
                    'cp38', 'zakat'
                ],
                item.get('type')
            ) &&
            (item.get('isGross') || item.get('isNet'))
        );
    }

    isOther() {
        const item = this.getPayrollItem();
        return (
            item &&
            !item.get('isGross') &&
            !item.get('isNet') &&
            item.get('type') !== 'unpaid_leave'
        );
    }

    getRealAmount() {
        const item = this.getPayrollItem();
        const multiplier = (
            _.contains(
                ['base', 'bonus', 'addition', 'benefit', 'overtime'],
                item.get('type')
            ) ? 1 : -1
        );
        return ((this.get('amount') || 0) * multiplier);
    }

    createOvertimeInfo(payroll = null, employee = null) {
        if (!this.isOvertime()) {
            return Promise.resolve(this);
        }

        return (employee ? Promise.resolve(employee) : this.fetchEmployee())
            .then(employee => {
                if (!employee) {
                    return 0;
                }

                const date = payroll && payroll.id === this.get('payrollId') ?
                    payroll.getPeriod() : moment();

                return employee.getHourlyWage(date);
            })
            .then(hourlyWage => {
                const overtimeFactor = this.getPayrollItem()
                    .get('overtimeFactor') || 1;
                if (!excludedOvertime.includes(this.getPayrollItem().get('name'))) {
                    const unit = this.get('unit');
                    const rate = new Decimal(hourlyWage)
                        .times(overtimeFactor)
                        .toDP(2, Decimal.ROUND_HALF_UP);

                    this.set({
                        unit,
                        rate: rate.toNumber(),
                        amount: new Decimal(unit)
                            .times(rate)
                            .toDP(2, Decimal.ROUND_HALF_UP)
                            .toNumber(),
                        additionalInformation: {
                            overtimeFactor: overtimeFactor,
                            hourlyWage: hourlyWage
                        }
                    });
                }

                return this;
            });
    }
}



// WEBPACK FOOTER //
// ./app/new-models/payroll-detail.js